<template>
	<div>
		<div class="flex xs-12 xs-between mt6">
			<div class="fs4 lh125 fw7 c1 mb2">{{ $t("facts") }}</div>
			<j-button
				type="tertiary"
				@click="$modal.show(`addFact${id}`)"
				v-can="['writeTransactions']"
			>
				<crm-icon name="add" color="c2" size="small" />
				{{ $t("add") }}
			</j-button>
		</div>

		<!-- Body -->
		<div>
			<j-expendable-box
				v-if="facts.length > 0"
				class="mb2 bg5 b1 r2 bc7 fs3 c6"
			>
				<div slot="header" class="flex pl3 c2 fw7" style="height: 40px; align-items: center">
					<p>{{ `${$t("facts_count")}: ${facts.length}` }}</p>
				</div>
				<div slot="body" v-for="(item, i) in facts" :key="i" class="bt1 bc7">
					<div class="flex xs-12 p3">
						<!-- Label -->
						<div class="flex xs-6 xs-left xs-middle fw7 c6">
							{{ item.label | unCamelCase }}
						</div>
						<!-- Body -->
						<div class="flex xs-3 xs-left xs-middle" v-if="item.value">
							<!-- If array -->
							<ul class="pl5" v-if="item.value.arrayValue">
								<li v-for="(listItem, j) in item.value.arrayValue" :key="j">
									{{ listItem.stringValue }}
								</li>
							</ul>

							<!-- If object -->
							<div v-else-if="item.value.objectValue">
								<span class="cu2 c2 fw7" @click="onPreview(item.value)"
									>See Object Value</span
								>
							</div>

							<!-- Else -->
							<div class="c1" v-else>{{ parseFactValue(item.value) }}</div>
						</div>

						<!-- Actions -->
						<div class="flex xs-3 xs-right xs-middle lh125">
							<crm-icon
								name="edit"
								size="normal"
								color="c6 ml4"
								class="cu2"
								@click.native="handleUpdate(item)"
							/>
							<crm-icon
								name="delete"
								size="normal"
								color="cError"
								class="cu2"
								@click.native="handleDeleteFact(item.id)"
							/>
						</div>
					</div>
				</div>
			</j-expendable-box>

			<!-- Empty -->
			<j-card v-if="facts.length === 0" class="mb2 bg5 p2 ta2 fs3 c1">
				{{ $t("errors.no_data") }}
			</j-card>
		</div>

		<!-- Add Modal -->
		<core-modal :name="`addFact${id}`" :headline="$t('add_fact')">
			<fact-form type="add" :isLoading="isLoading" @submit="handleAddFact" />
		</core-modal>

		<!-- Update Modal -->
		<core-modal :name="`updateFact${id}`" :headline="$t('update_fact')">
			<fact-form
				type="update"
				:isLoading="isLoading"
				:initValues="factUpdateValues"
				@submit="handleUpdateFact"
			/>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard/"
import JExpendableBox from "@/components/JExpendableBox/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import FactForm from "./FactForm"

import api from "../api"
import dayjs from "dayjs"

export default {
	props: {
		id: String,
		facts: Array
	},

	components: {
		JCard,
		JExpendableBox,
		JButton,
		CrmIcon,
		CoreModal,
		FactForm
	},

	data() {
		return {
			isLoading: false,
			factUpdateId: null,
			factUpdateValues: null
		}
	},

	methods: {
		parseFactValue(value) {
			if (!value || value.arrayValue) return null
			if (value.stringValue) return value.stringValue
			if (value.numberValue) return value.numberValue
			if (value.dateValue) return dayjs(value.dateValue).format("DD/MM/YYYY")
			if (value.booleanValue === true) return this.$t("yes")
			if (value.booleanValue !== true) return this.$t("no")
		},

		handleUpdate(item) {
			this.$modal.show(`updateFact${this.id}`)

			// get selected fact option by checking which field is a non null
			const selectedFactOption = Object.entries(item.value)
				.filter(item => item[1] !== null)
				.map(item => item[0])
				.toString()

			this.factUpdateId = item.id

			this.factUpdateValues = {
				factOptions: selectedFactOption,
				type: item.label,
				value: item.value[selectedFactOption]
			}
		},

		async handleAddFact(data) {
			this.isLoading = true

			const res = await api.addFactToTransaction({
				transactionId: this.id,
				input: data
			})

			this.isLoading = false

			// Error
			if (res?.data?.addFactToTransaction?.status !== "success") {
				this.$store.dispatch("showError", res.errors)
				return
			}

			this.$emit("added")
			this.$modal.hide(`addFact${this.id}`)
		},

		async handleUpdateFact(data) {
			this.isLoading = true

			const res = await api.updateTransactionFact({
				transactionId: this.id,
				factId: this.factUpdateId,
				input: data
			})

			this.isLoading = false

			// Error
			if (res?.data?.updateTransactionFact?.status !== "success") {
				this.$store.dispatch("showError", res.errors)
				return
			}

			this.$emit("updated")
			this.$modal.hide(`updateFact${this.id}`)
			this.factUpdateId = null
		},

		async handleDeleteFact(data) {
			const res = await api.removeFactFromTransaction({
				transactionId: this.id,
				factId: data
			})

			// Error
			if (res?.data?.removeFactFromTransaction?.status !== "success") {
				this.$store.dispatch("showError", res.errors)
				return
			}

			this.$emit("deleted")
		}
	}
}
</script>
